import { FC, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSpotifyPlayback } from '../contexts/SpotifyPlaybackContext';

const PlayerPage: FC = () => {
  const { isAuthenticated, login } = useAuth();
  const { playbackState, queue, queueContext, error, status, togglePlay, nextTrack, previousTrack, seek, reconnect, refreshQueue, playTrack, playTrackInContext } = useSpotifyPlayback();
  const [seekPreview, setSeekPreview] = useState<number | null>(null);
  const currentTrackId = playbackState?.track_window.current_track.id;

  useEffect(() => setSeekPreview(null), [currentTrackId]);

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[65vh] p-4">
        <h1 className="text-3xl font-bold mb-4">Player</h1>
        <p className="mb-8 text-center">Log in to control Spotify and view your queue.</p>
        <button onClick={login} className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full">Login with Spotify</button>
      </div>
    );
  }

  if (!playbackState) {
    return (
      <div className="page-container max-w-3xl mx-auto">
        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center shadow-xl">
          <h1 className="text-3xl font-bold mb-3">Spotify Player</h1>
          <p className="text-gray-300 mb-6">{status === 'loading' ? 'Connecting to Spotify…' : error || 'Start a track from your library to see it here.'}</p>
          {error && <button onClick={reconnect} className="bg-green-500 text-gray-950 font-bold px-6 py-3 rounded-full">Retry</button>}
        </div>
      </div>
    );
  }

  const track = playbackState.track_window.current_track;
  const albumImage = track.album.images?.[0]?.url;
  const isPlaying = !playbackState.paused;
  const displayedPosition = seekPreview ?? playbackState.position;
  const handleTrackClick = async (trackUri: string, trackId: string) => {
    if (queueContext === 'album' && playbackState.context.uri) {
      await playTrackInContext({ trackUri, albumUri: playbackState.context.uri });
    } else {
      await playTrack(trackId);
    }
  };

  return (
    <div className="player-page">
      <section className="min-h-[calc(100vh-72px)] bg-gradient-to-b from-gray-800 to-gray-950 text-white overflow-hidden">
        <div className="grid md:grid-cols-[18rem_1fr] gap-6 p-5 sm:p-8">
          {albumImage && <img src={albumImage} alt={`${track.album.name} cover`} className="w-full max-w-72 aspect-square object-cover rounded-xl shadow-2xl mx-auto" />}
          <div className="flex flex-col justify-end min-w-0">
            <p className="uppercase tracking-widest text-sm text-green-400 font-bold mb-2">Now playing</p>
            <h1 className="text-3xl sm:text-5xl font-black leading-tight mb-3">{track.name}</h1>
            <p className="text-xl text-gray-200">{track.artists.map(artist => artist.name).join(', ')}</p>
            <p className="text-gray-400 mb-7">{track.album.name}</p>
            <input
              type="range"
              min={0}
              max={Math.max(playbackState.duration, 1)}
              step={1000}
              value={Math.min(displayedPosition, playbackState.duration)}
              onChange={(event) => setSeekPreview(Number(event.target.value))}
              onPointerUp={(event) => {
                void seek(Number(event.currentTarget.value));
                setSeekPreview(null);
              }}
              onKeyUp={(event) => {
                void seek(Number(event.currentTarget.value));
                setSeekPreview(null);
              }}
              onPointerCancel={() => setSeekPreview(null)}
              className="w-full h-2 accent-green-500 cursor-pointer"
              aria-label="Playback position"
            />
            <div className="flex justify-between text-sm text-gray-400 mt-1"><span>{formatTime(displayedPosition)}</span><span>{formatTime(playbackState.duration)}</span></div>
            <div className="flex items-center justify-center gap-6 my-5">
              <button onClick={previousTrack} className="text-2xl min-w-12" aria-label="Previous track">⏮</button>
              <button onClick={togglePlay} className="grid place-items-center bg-white text-gray-950 rounded-full w-16 h-16 text-2xl" aria-label={isPlaying ? 'Pause' : 'Play'}>{isPlaying ? '⏸' : '▶'}</button>
              <button onClick={nextTrack} className="text-2xl min-w-12" aria-label="Next track">⏭</button>
            </div>
            <a href={`https://open.spotify.com/track/${track.id}`} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center justify-center self-center rounded-full border border-green-400 px-6 font-bold text-green-300 hover:bg-green-400 hover:text-gray-950">Open in Spotify</a>
          </div>
        </div>
        <div className="border-t border-gray-700 bg-black/20 p-5 sm:p-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl font-bold">{queueContext === 'album' ? 'Current album' : 'Up next'}</h2>
              <p className="text-sm text-gray-400">{queue.length} track{queue.length === 1 ? '' : 's'}</p>
            </div>
            <button onClick={refreshQueue} className="rounded-full bg-gray-700 hover:bg-gray-600 px-4 py-2 text-sm font-semibold">Refresh</button>
          </div>
          {queue.length ? (
            <ul className="divide-y divide-gray-800">
              {queue.map((queuedTrack, index) => (
                <li key={`${queuedTrack.uri}-${index}`}>
                  <button
                    onClick={() => handleTrackClick(queuedTrack.uri, queuedTrack.id)}
                    aria-current={queuedTrack.id === track.id ? 'true' : undefined}
                    className={`w-full text-left grid ${queueContext === 'album' ? 'grid-cols-[2rem_1fr_auto]' : 'grid-cols-[1fr_auto]'} gap-3 items-center py-3 px-2 rounded hover:bg-white/10 ${queuedTrack.id === track.id ? 'bg-green-500/15 text-green-200' : ''}`}
                  >
                    {queueContext === 'album' && <span className="text-gray-500 text-right">{queuedTrack.track_number || index + 1}</span>}
                    <div className="min-w-0"><p className="font-semibold truncate">{queuedTrack.name}</p><p className="text-sm text-gray-400 truncate">{queuedTrack.artists.map(artist => artist.name).join(', ')} · {queuedTrack.album.name}</p></div>
                    <span className="text-sm text-gray-500">{formatTime(queuedTrack.duration_ms)}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : <p className="text-gray-400 py-8 text-center">Spotify has no upcoming tracks in the queue.</p>}
        </div>
      </section>
    </div>
  );
};

function formatTime(ms = 0): string {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export default PlayerPage;
